
import { createApi } from "@reduxjs/toolkit/query/react";
import { application, telemetry } from "globals/endpoints";
import {
  normalizeAlert,
  normalizeDevice,
  normalizeLocation,
  normalizeSummaryRow,
} from "layouts/nms/widgets/CoralICCCView/model/normalize";

// location/list defaults pageSize to 2 server-side (SearchRequest) — Map View's
// tab bar needs every location, so this must always be overridden explicitly.
const LOCATION_LIST_PAGE_SIZE = 1000;

// Adapts an axios instance to RTK Query's baseQuery contract.
const axiosBaseQuery =
  (instance) =>
  async ({ url, method = "get", data, params }) => {
    try {
      const result = await instance({ url, method, data, params });
      return { data: result.data };
    } catch (axiosError) {
      return {
        error: {
          status: axiosError.response?.status,
          data: axiosError.response?.data || axiosError.message,
        },
      };
    }
  };

// The dashboard spans two services. RTK Query allows a single baseQuery, so we
// route per-request via an explicit `target` field.
const dualBaseQuery = (args, api, extraOptions) => {
  const instance = args.target === "services" ? application : telemetry;
  return axiosBaseQuery(instance)(args, api, extraOptions);
};

// Sum totals by category code (defensive — the SQL already groups, but a merged
// deployment could surface the same code twice).
const aggregateByCode = (rows = []) => {
  const map = new Map();
  rows.map(normalizeSummaryRow).forEach(({ code, total }) => {
    map.set(code, (map.get(code) || 0) + total);
  });
  return Array.from(map, ([code, total]) => ({ code, total }));
};

export const icccApi = createApi({
  reducerPath: "icccApi",
  baseQuery: dualBaseQuery,
  tagTypes: ["assets", "alerts", "devices"],
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    // ---- Asset aggregates (telemetry) ----
    getDeviceCount: builder.query({
      query: () => ({ target: "telemetry", url: "asset/count" }),
      transformResponse: (r) => Number(r?.data?.deviceCount ?? 0),
      providesTags: ["assets"],
    }),

    // Per-category totals — drives the overall pie + group tiles.
    getAssetSummary: builder.query({
      query: () => ({ target: "telemetry", url: "asset/summary/all" }),
      transformResponse: (r) => aggregateByCode(r?.data || []),
      providesTags: ["assets"],
    }),

    // Per-category not-working (status=false) totals. Authoritative unhealthy
    // count — equals Σ(severity ≥ 1) in the schema, so healthy = total − this.
    getAssetSummaryNotWorking: builder.query({
      query: () => ({ target: "telemetry", url: "asset/summary/not-working" }),
      transformResponse: (r) => aggregateByCode(r?.data || []),
      providesTags: ["assets"],
    }),

    // ---- Alerts (telemetry) ----
    getActiveAlerts: builder.query({
      query: () => ({ target: "telemetry", url: "alert/active/list" }),
      transformResponse: (r) => (r?.data || []).map(normalizeAlert),
      providesTags: ["alerts"],
    }),

    getActiveAlertSummary: builder.query({
      query: () => ({ target: "telemetry", url: "alert/active/summary" }),
      transformResponse: (r) => r?.data || [],
      providesTags: ["alerts"],
    }),

    getActiveAlertsForAsset: builder.query({
      query: (assetId) => ({ target: "telemetry", url: `alert/asset/active/${assetId}` }),
      transformResponse: (r) => (r?.data || []).map(normalizeAlert),
      providesTags: (result, error, assetId) => [{ type: "alerts", id: assetId }],
    }),

    getAlertHistoryForAsset: builder.query({
      query: (assetId) => ({ target: "telemetry", url: `alert/asset/history/${assetId}` }),
      transformResponse: (r) => (r?.data || []).map(normalizeAlert),
      providesTags: (result, error, assetId) => [{ type: "alerts", id: `history-${assetId}` }],
    }),

    // Filterable alert detail (paged) — POST body is a SearchRequest.
    getAlertDetailFilter: builder.query({
      query: (searchRequest) => ({
        target: "telemetry",
        url: "alertDetail/getFilterDataV2",
        method: "post",
        data: searchRequest,
      }),
      transformResponse: (r) => ({
        rows: (r?.data?.currentPageData || []).map(normalizeAlert),
        total: Number(r?.data?.totalRecords ?? 0),
      }),
      providesTags: ["alerts"],
    }),

    // ---- Device grid (services-app) ----
    // Keyed query (not a mutation) so each page/search/sort combination caches
    // and polls independently.
    getDevices: builder.query({
      query: (searchRequest) => ({
        target: "services",
        url: "SCADAAsset/list",
        method: "post",
        data: searchRequest,
      }),
      transformResponse: (r) => ({
        rows: (r?.data?.currentPageData || []).map(normalizeDevice),
        total: Number(r?.data?.totalRecords ?? 0),
      }),
      providesTags: ["devices"],
    }),

    // ---- Map View device feed (telemetry) ----
    // services SCADAAsset/list has no uuid, and the per-asset alert endpoints
    // require it — the Site Map plots every device at once (no search/sort/
    // paging), so it reads from telemetry's unpaged list instead, which
    // carries uuid alongside id.
    getMapDevices: builder.query({
      query: () => ({ target: "telemetry", url: "SCADAAsset/list" }),
      transformResponse: (r) => (r?.data || []).map(normalizeDevice),
      providesTags: ["devices"],
    }),

    // Category code <-> display label map (telemetry). Group tiles/summary use
    // the m_category description as the label, but scada_assets.category stores
    // the code; this lets the device grid translate a selected group to its code.
    getCategories: builder.query({
      query: () => ({ target: "telemetry", url: "category/list" }),
      transformResponse: (r) =>
        (r?.data || []).map((c) => ({ code: c.code, description: c.description })),
      providesTags: ["assets"],
    }),

    // ---- Location Master (services-app) ----
    // Map View's tabs — one per location with a non-empty backgroundImageURL.
    // POST is a SearchRequest body (Location Master has no GET/list); pageSize
    // must be overridden or the API silently caps the result at 2 rows.
    getLocations: builder.query({
      query: () => ({
        target: "services",
        url: "location/list",
        method: "post",
        data: {
          currentPage: 0,
          pageSize: LOCATION_LIST_PAGE_SIZE,
          sortDirection: "asc",
          sortBy: "name",
          search: "",
          advancedFilters: [],
        },
      }),
      transformResponse: (r) => (r?.data?.currentPageData || []).map(normalizeLocation),
      providesTags: ["assets"],
    }),

    // ---- Marker placement (telemetry) ----
    // Persists a device's drag-drop position. Coordinates are stored as raw,
    // unvalidated strings — callers must send the normalised-% format.
    updateCoordinate: builder.mutation({
      query: ({ id, xCoordinate, yCoordinate }) => ({
        target: "telemetry",
        url: "asset/updateCoordinate",
        method: "post",
        data: { id, xCoordinate, yCoordinate },
      }),
      invalidatesTags: ["devices"],
    }),

    // Reverses a placement — clears stored coordinates so the device
    // returns to the Unplaced list.
    clearCoordinate: builder.mutation({
      query: ({ id }) => ({
        target: "telemetry",
        url: "asset/clearCoordinate",
        method: "post",
        data: { id },
      }),
      invalidatesTags: ["devices"],
    }),
  }),
});

export const {
  useGetDeviceCountQuery,
  useGetAssetSummaryQuery,
  useGetAssetSummaryNotWorkingQuery,
  useGetActiveAlertsQuery,
  useGetActiveAlertSummaryQuery,
  useGetActiveAlertsForAssetQuery,
  useGetAlertHistoryForAssetQuery,
  useGetAlertDetailFilterQuery,
  useGetDevicesQuery,
  useGetMapDevicesQuery,
  useGetCategoriesQuery,
  useGetLocationsQuery,
  useUpdateCoordinateMutation,
  useClearCoordinateMutation,
} = icccApi;
